import { useTranslation } from 'react-i18next';
import { SearchResult, SearchResults } from '@/services/search.service';
import { SearchResultRow } from './SearchResultRow';

interface SearchResultsPanelProps {
  results: SearchResults | null;
  query: string;
  highlightedIndex: number;
  onSelect: (path: string) => void;
}

export function SearchResultsPanel({
  results,
  query,
  highlightedIndex,
  onSelect,
}: SearchResultsPanelProps) {
  const { t } = useTranslation();

  const members = results?.members ?? [];
  const leads = results?.leads ?? [];
  const hasMembers = members.length > 0;
  const hasLeads = leads.length > 0;
  const isEmpty = !hasMembers && !hasLeads;

  // Flat index: members occupy [0, members.length-1]; leads occupy [members.length, members.length+leads.length-1]
  const getMemberHighlight = (i: number) => highlightedIndex === i;
  const getLeadHighlight = (i: number) =>
    highlightedIndex === members.length + i;

  return (
    <div
      id="global-search-results"
      role="listbox"
      className="absolute top-full left-0 mt-1 w-full min-w-[384px] max-h-96 overflow-y-auto z-50 rounded-md border border-[var(--border-default)] bg-[var(--background-card)] shadow-[0_4px_16px_rgba(0,0,0,0.4)]"
    >
      {isEmpty ? (
        <div className="py-m text-center text-body-s text-neutral-500">
          {t('globalSearch.noResults', { query })}
        </div>
      ) : (
        <>
          {hasMembers && (
            <div role="group" aria-label={t('globalSearch.sectionMembers')}>
              <div className="px-xs py-3xs bg-[var(--color-forest)] text-body-xs font-semibold text-[var(--text-on-forest)]">
                {t('globalSearch.sectionMembers')}
              </div>
              {members.map((member: SearchResult, i: number) => (
                <SearchResultRow
                  key={member._id}
                  name={`${member.firstName} ${member.lastName}`}
                  email={member.email}
                  isHighlighted={getMemberHighlight(i)}
                  onClick={() => onSelect(`/members/${member._id}/view`)}
                />
              ))}
            </div>
          )}
          {hasMembers && hasLeads && (
            <div className="border-t border-[var(--border-default)]" />
          )}
          {hasLeads && (
            <div role="group" aria-label={t('globalSearch.sectionLeads')}>
              <div className="px-xs py-3xs bg-[var(--color-forest)] text-body-xs font-semibold text-[var(--text-on-forest)]">
                {t('globalSearch.sectionLeads')}
              </div>
              {leads.map((lead: SearchResult, i: number) => (
                <SearchResultRow
                  key={lead._id}
                  name={`${lead.firstName} ${lead.lastName}`}
                  email={lead.email}
                  isHighlighted={getLeadHighlight(i)}
                  onClick={() => onSelect(`/leads/${lead._id}/view`)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
